// ============================================
// useRaizStream — assina o SSE ao vivo (GET /api/raiz/stream) e faz o dashboard
// reagir instantaneamente, mantendo o polling de 30s como rede de segurança.
//
// Defensivo por design: se o endpoint não existir ou cair, o EventSource dispara
// onerror; nós fechamos e reagendamos a reconexão com backoff exponencial + jitter,
// silenciosamente (o polling continua servindo os dados). Monta-se UMA vez, no
// AppShell.
//
// Eventos:
//  - 'reading'        → patch leve do cache ['raiz','devices'] (last_reading/
//                       last_seen_at/presence do device) para refletir na hora.
//  - 'excursion_open' → invalida ['raiz','excursions'] (fila + resumo do sino),
//                       ['raiz','units'] e ['raiz','unit'] (a árvore de Unidades
//                       fixa quem tem excursão aberta) e mostra um toast global
//                       discreto.
// ============================================

import { createElement, Fragment, useEffect, useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '../components/ToastProvider'
import type {
  RaizDevicesResponse, RaizDeviceOverview, StreamReadingEvent, StreamExcursionOpenEvent,
} from '../pages/Raiz/types'

const STREAM_URL = '/api/raiz/stream'
const BASE_RECONNECT_MS = 1_000
const MAX_RECONNECT_MS = 30_000

/**
 * Aplica uma leitura recém-chegada ao overview de um device (imutável).
 *
 * Leitura MAIS VELHA que a da tela é descartada (devolve o mesmo objeto): um
 * lote atrasado — buffer do anel descarregando, dois POSTs concorrentes — fazia
 * o card voltar para o valor antigo marcado "Ao vivo" até o próximo polling.
 *
 * O stream só carrega temperatura e umidade: o resto da última leitura (solo,
 * pressão, bateria) é preservado. Montar o objeto do zero apagava a bateria e a
 * pressão do card a cada leitura ao vivo, até o polling devolvê-las.
 */
export function applyReading (d: RaizDeviceOverview, ev: StreamReadingEvent): RaizDeviceOverview {
  const evMs = Date.parse(ev.ts)
  const curMs = d.last_reading != null ? Date.parse(d.last_reading.ts) : Number.NaN
  if (!Number.isNaN(evMs) && !Number.isNaN(curMs) && evMs <= curMs) return d
  return {
    ...d,
    last_seen_at: ev.ts,
    presence: ev.presence ?? d.presence,
    last_reading: {
      ...d.last_reading,
      ts: ev.ts,
      temperature: ev.temperature,
      humidity: ev.humidity,
      soil: d.last_reading?.soil ?? null,
    },
  }
}

/** Rastro que coalesce a rajada de excursion_open numa só invalidação de unidades. */
const UNITS_INVALIDATE_DEBOUNCE_MS = 500

export function useRaizStream (options: { enabled?: boolean, companyId?: string | null } = {}): void {
  // F4 — companyId participa das DEPS do efeito: o servidor congela a empresa da
  // sessão no connect do EventSource, então trocar de empresa precisa fechar e
  // reabrir o stream (senão o painel da empresa B receberia eventos ao vivo da A).
  const { enabled = true, companyId = null } = options
  const qc = useQueryClient()
  const { showToast } = useToast()

  // Refs estáveis para os callbacks — evitam recriar o efeito a cada render.
  const qcRef = useRef(qc)
  const toastRef = useRef(showToast)
  qcRef.current = qc
  toastRef.current = showToast

  useEffect(() => {
    if (!enabled) return
    if (typeof EventSource === 'undefined') return // ambiente sem SSE — só polling

    let es: EventSource | null = null
    let attempt = 0
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let closed = false
    let unitsInvalidateTimer: ReturnType<typeof setTimeout> | null = null

    const onReading = (raw: MessageEvent): void => {
      let ev: StreamReadingEvent
      try {
        ev = JSON.parse(raw.data)
      } catch {
        return
      }
      if (ev?.device == null) return
      qcRef.current.setQueryData<RaizDevicesResponse>(['raiz', 'devices'], (prev) => {
        if (prev == null) return prev
        let changed = false
        const devices = prev.devices.map((d) => {
          if (d.mac !== ev.device) return d
          const next = applyReading(d, ev)
          if (next !== d) changed = true
          return next
        })
        return changed ? { ...prev, devices } : prev
      })
    }

    const onExcursionOpen = (raw: MessageEvent): void => {
      let ev: StreamExcursionOpenEvent
      try {
        ev = JSON.parse(raw.data)
      } catch {
        return
      }
      // Fila global + resumo do sino: invalidação por prefixo pega ambos.
      void qcRef.current.invalidateQueries({ queryKey: ['raiz', 'excursions'] })
      // Unidades: o `open_excursions` da lista é o que FIXA a unidade no ramo
      // recolhido (e acende o rollup do pai), e o detalhe aberto mostra a mesma
      // excursão. Esperar o polling de 30 s deixaria o toast gritando enquanto a
      // árvore ainda mostra a câmara em paz. Prefixo ['raiz','unit'] pega o
      // detalhe de qualquer id sem precisar saber qual está aberto.
      //
      // Coalescido com rastro de 500 ms: uma queda de energia num CD abre
      // dezenas de excursões no MESMO tick do scheduler, e cada invalidação
      // cancelaria o fetch em voo e dispararia outro (o listUnits é o payload
      // mais pesado — geojson dos talhões). O último fetch começa depois do
      // último evento da rajada.
      if (unitsInvalidateTimer != null) clearTimeout(unitsInvalidateTimer)
      unitsInvalidateTimer = setTimeout(() => {
        unitsInvalidateTimer = null
        void qcRef.current.invalidateQueries({ queryKey: ['raiz', 'units'] })
        void qcRef.current.invalidateQueries({ queryKey: ['raiz', 'unit'] })
      }, UNITS_INVALIDATE_DEBOUNCE_MS)
      const nome = ev?.unit_name?.trim() || ev?.device || 'unidade'
      // O crítico grita E aponta o caminho: toast com ação direta para a fila.
      toastRef.current(
        createElement(
          Fragment,
          null,
          `Nova excursão: ${nome} — `,
          createElement(
            RouterLink,
            { to: '/events/excursions', style: { color: 'inherit', fontWeight: 700, textDecoration: 'underline' } },
            'ver fila',
          ),
        ),
        { severity: 'warning', duration: 8000 },
      )
    }

    const scheduleReconnect = (): void => {
      if (closed) return
      const delay = Math.min(BASE_RECONNECT_MS * 2 ** attempt, MAX_RECONNECT_MS)
      // jitter ±20% para não sincronizar reconexões de vários clientes.
      const jittered = delay * (0.8 + Math.random() * 0.4)
      attempt += 1
      reconnectTimer = setTimeout(connect, jittered)
    }

    function connect (): void {
      if (closed) return
      try {
        es = new EventSource(STREAM_URL, { withCredentials: true })
      } catch {
        scheduleReconnect()
        return
      }
      es.addEventListener('open', () => { attempt = 0 })
      es.addEventListener('reading', onReading as EventListener)
      es.addEventListener('excursion_open', onExcursionOpen as EventListener)
      es.addEventListener('error', () => {
        // onerror silencioso: fecha e reagenda. O polling de 30s segue de fallback.
        es?.close()
        es = null
        scheduleReconnect()
      })
    }

    connect()

    return () => {
      closed = true
      if (unitsInvalidateTimer != null) clearTimeout(unitsInvalidateTimer)
      if (reconnectTimer != null) clearTimeout(reconnectTimer)
      es?.close()
      es = null
    }
  }, [enabled, companyId])
}
